import { FC } from 'react';

import { Box, Divider, Typography } from '@mui/material';
import { isNil } from '@npi/buddy-utils';
import { useField } from 'formik';

import MainCard from 'src/components/MainCard/MainCard';
import { useProductOrderContext } from 'src/contexts/ProductOrderProvider';
import { useServiceOrderContext } from 'src/contexts/ServiceOrderProvider';

import { OrderType, ServiceProducts } from '../ServiceProducts';
import { PricingServiceTypes } from '../ServiceType';

export type SelectedProducts = {
    primary: any[];
    secondary: any[];
};

interface ServiceOrdersProps {
    serviceType: PricingServiceTypes;
    order: any;
    selectedOffer: number;
    isEditingDisabled?: boolean;
}

export const ServiceOrders: FC<ServiceOrdersProps> = ({
    serviceType,
    order,
    selectedOffer,
    isEditingDisabled = false,
}) => {
    const { offerProducts, setTotalOrderPrice, offerData, partnerId, contractTerm } =
        useServiceOrderContext();
    const [{ value: serviceOrder }, , { setValue: setServiceOrder }] = useField({
        name: 'order',
        defaultValue: order,
    });
    const { addOrderItem } = useProductOrderContext();

    const handleSelectProduct = (id: number, type: OrderType) => {
        const productPricing = { type: null, price: null, options: null };
        offerData
            .filter((offerItem) => {
                return !isNil(offerItem);
            })
            .forEach((offer) => {
                if (offer && offer.id == selectedOffer) {
                    if (
                        offer[`${type}Products`].some((offerProduct) => {
                            return offerProduct.id === id;
                        })
                    ) {
                        // Find the pricing for this product from the offer where this product is in
                        const pricing = offer?.pricings?.find((pricing) => {
                            return (
                                pricing?.catalogue?.toLowerCase() === type &&
                                pricing?.product?.id === id
                            );
                        });
                        productPricing.type = pricing?.type;
                        productPricing.price =
                            pricing?.prices?.monthly?.lowest + pricing?.prices?.oneOff?.lowest;
                        productPricing.options = pricing?.prices?.options;
                    }
                }
            });

        let selectedProduct = offerProducts?.[`${type}Products`]?.filter((offerProduct) => {
            return offerProduct?.id === id;
        })?.[0];

        if (
            !serviceOrder?.[type]?.some((alreadySelectedProducts) => {
                return alreadySelectedProducts?.id === id;
            })
        ) {
            const newPrimaryProductsToAdd = offerProducts.primaryProducts?.map((product) => {
                if (product?.id === id && type === 'primary') {
                    product = { ...product, ...productPricing };

                    addOrderItem({
                        id: product.id,
                        quantity: 1,
                        oneOffPrice: product?.options?.oneOff?.[0]?.sales ?? 0,
                        monthlyPrice: product?.options?.monthly?.[0]?.sales ?? 0,
                        type: 'PRIMARY',
                        description: product?.description || product?.name,
                    });
                    return product;
                }
            });

            const newSecondaryProductsToAdd = offerProducts.secondaryProducts?.map((product) => {
                if (product.id === id && type === 'secondary') {
                    product = { ...product, ...productPricing };

                    addOrderItem({
                        id: product.id,
                        quantity: 1,
                        oneOffPrice: product?.options?.oneOff?.[0]?.sales ?? 0,
                        monthlyPrice: product?.options?.monthly?.[0]?.sales ?? 0,
                        type: 'SECONDARY',
                        description: product?.description || product?.name,
                    });

                    return product;
                }
            });

            setServiceOrder({
                primary: [
                    ...(serviceOrder?.primary ?? []),
                    ...(newPrimaryProductsToAdd ?? []),
                ].filter((product) => {
                    return product;
                }),
                secondary: [
                    ...(serviceOrder?.secondary ?? []),
                    ...(newSecondaryProductsToAdd ?? []),
                ].filter((product) => {
                    return product;
                }),
            });

            selectedProduct = { ...selectedProduct, ...productPricing };
        }

        setTotalOrderPrice((prev) => {
            return {
                totalMonthly: [
                    ...prev.totalMonthly,
                    {
                        id,
                        price: selectedProduct?.options?.monthly[0]?.sales,
                        quantity: 1,
                    },
                ],
                totalOneOff: [
                    ...prev.totalOneOff,
                    {
                        id,
                        price: selectedProduct?.options?.oneOff[0]?.sales,
                        quantity: 1,
                    },
                ],
            };
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={3}>
            <MainCard>
                {/* Card heading */}
                <Typography fontSize="24px" color="#575757" marginBottom="8px">
                    Service Order
                </Typography>
                <Typography fontSize="14px" color="#575757" marginBottom="8px">
                    Select Service Order
                </Typography>
                <Divider
                    sx={{
                        borderColor: 'warning.main',
                        marginBottom: '34px',
                    }}
                />
                {/* Primary Order section */}
                <Typography fontSize="18px" color="#1D1D1B" marginBottom="8px">
                    Primary Order
                </Typography>
                <ServiceProducts
                    products={offerProducts.primaryProducts || []}
                    orderType="primary"
                    selectProduct={handleSelectProduct}
                    selectedProducts={serviceOrder}
                    serviceType={serviceType}
                    contractTerm={contractTerm}
                    partnerId={partnerId}
                    disabled={isEditingDisabled}
                />
                <Divider
                    sx={{
                        my: '24px',
                    }}
                />
                {/* Secondary Order section */}
                <Typography fontSize="18px" color="#1D1D1B" marginBottom="8px">
                    Secondary Order
                </Typography>
                <ServiceProducts
                    products={offerProducts.secondaryProducts || []}
                    orderType="secondary"
                    selectProduct={handleSelectProduct}
                    selectedProducts={serviceOrder}
                    serviceType={serviceType}
                    contractTerm={contractTerm}
                    partnerId={partnerId}
                    disabled={isEditingDisabled}
                />
            </MainCard>
        </Box>
    );
};
